import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Select,
  DatePicker,
  Button,
  Input,
  Spin,
  Pagination,
  Typography,
  Empty,
  message,
  Space,
  Modal
} from 'antd';
import {
  ReloadOutlined,
  FilterOutlined,
  PlusOutlined,
  ClearOutlined,
  ExclamationCircleOutlined,
  GoogleOutlined,
  FacebookOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { auditAPI } from '../../services/api';
import AuditCard from './components/AuditCard';
import AuditModal from './components/AuditModal';
import BrandAuditModal from '../../components/BrandAuditModal';
import api from '../../services/api';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const Audits = () => {
  const [loading, setLoading] = useState(false);
  const [audits, setAudits] = useState([]);
  const [brands, setBrands] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  
  // Filters
  const [filters, setFilters] = useState({
    brandId: undefined,
    auditType: undefined,
    status: undefined,
    dateRange: undefined,
    search: ''
  });

  // Modal
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  
  // New Audit Modal
  const [newAuditModalVisible, setNewAuditModalVisible] = useState(false);
  const [selectedBrandForAudit, setSelectedBrandForAudit] = useState(null);
  const [brandCapabilities, setBrandCapabilities] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    loadBrands();
    loadAudits();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadAudits();
  }, [pagination.current, pagination.pageSize, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBrands = async () => {
    try {
      const response = await api.get('/brands');
      setBrands(response.data || []);
    } catch (error) {
      console.error('Brands loading error:', error);
      setBrands([]);
    }
  };

  const loadAudits = async () => {
    try {
      setLoading(true);
      
      const params = {
        page: pagination.current,
        limit: pagination.pageSize
      };

      if (filters.brandId) params.brandId = filters.brandId;
      if (filters.auditType) params.auditType = filters.auditType;
      if (filters.status) params.status = filters.status;
      if (filters.dateRange && filters.dateRange.length === 2) {
        params.dateFrom = filters.dateRange[0].format('YYYY-MM-DD');
        params.dateTo = filters.dateRange[1].format('YYYY-MM-DD');
      }

      const response = await auditAPI.getAllAudits(params);
      
      setAudits(response.data.audits || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0
      }));

    } catch (error) {
      console.error('Audits loading error:', error);
      message.error('Auditler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleSearch = (value) => {
    setFilters(prev => ({
      ...prev,
      search: value
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      brandId: undefined,
      auditType: undefined,
      status: undefined,
      dateRange: undefined,
      search: ''
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleViewDetails = (audit) => {
    setSelectedAudit(audit);
    setModalVisible(true);
  };

  const handlePaginationChange = (page, pageSize) => {
    setPagination(prev => ({
      ...prev,
      current: page,
      pageSize: pageSize
    }));
  };

  const handleNewAudit = async (brand) => {
    try {
      setAuditLoading(true);
      
      // Get brand audit capabilities
      const response = await auditAPI.getBrandAuditCapabilities(brand.id);
      
      if (!response.data.canAudit) {
        message.warning(`${brand.name} için audit yapılacak Google Ads veya Facebook Ads kodu bulunamadı.`);
        return;
      }
      
      setSelectedBrandForAudit(brand);
      setBrandCapabilities(response.data);
      setNewAuditModalVisible(true);
      
    } catch (error) {
      console.error('Brand capabilities error:', error);
      message.error('Marka bilgileri yüklenirken hata oluştu');
    } finally {
      setAuditLoading(false);
    }
  };

  const handleAuditSuccess = (auditResult) => {
    message.success(`${selectedBrandForAudit?.name} için audit başarıyla başlatıldı!`);
    setNewAuditModalVisible(false);
    setSelectedBrandForAudit(null);
    setBrandCapabilities(null);
    
    // Refresh audits list
    setTimeout(() => {
      loadAudits();
    }, 1000);
  };

  const handleAuditError = (error) => {
    console.error('Audit error:', error);
    message.error(error.message || 'Audit başlatılırken hata oluştu');
  };


  const handleCancelAudit = (audit) => {
    if (audit.status !== 'pending') {
      message.warning('Sadece bekleyen audit\'ler iptal edilebilir');
      return;
    }

    Modal.confirm({
      title: 'Audit\'i İptal Et',
      content: `"${audit.Brand?.name}" markasının ${audit.auditType} audit'ini iptal etmek istediğinizden emin misiniz?`,
      icon: <ExclamationCircleOutlined />,
      okText: 'İptal Et',
      cancelText: 'Vazgeç',
      okType: 'danger',
      onOk: async () => {
        try {
          setLoading(true);
          await auditAPI.cancelPendingAudit(audit.id);
          message.success('Audit başarıyla iptal edildi');
          loadAudits(); // Refresh list
        } catch (error) {
          console.error('Cancel audit error:', error);
          message.error(error.response?.data?.error || 'Audit iptal edilemedi');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleDeleteAudit = (audit) => {
    Modal.confirm({
      title: 'Audit\'i Sil',
      content: `"${audit.Brand?.name}" markasının bu audit kaydını kalıcı olarak silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Sil',
      cancelText: 'İptal',
      okType: 'danger',
      onOk: async () => {
        try {
          setLoading(true);
          await auditAPI.deleteAudit(audit.id);
          message.success('Audit başarıyla silindi');
          loadAudits(); // Refresh list
        } catch (error) {
          console.error('Delete audit error:', error);
          message.error(error.response?.data?.error || 'Audit silinemedi');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleToggleVisibility = async (audit) => {
    try {
      setLoading(true);
      const response = await auditAPI.toggleAuditVisibility(audit.id);
      message.success(response.data.message);
      loadAudits(); // Refresh list
    } catch (error) {
      console.error('Toggle visibility error:', error);
      message.error(error.response?.data?.error || 'Görünürlük değiştirilemedi');
    } finally {
      setLoading(false);
    }
  };


  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return '#52c41a';
      case 'error': return '#f5222d';
      case 'pending': return '#faad14';
      default: return '#d9d9d9';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'success': return 'Başarılı';
      case 'error': return 'Hatalı';
      case 'pending': return 'Beklemede';
      default: return 'Bilinmiyor';
    }
  };

  // Filter brands based on search
  const filteredAudits = audits.filter(audit => {
    if (!filters.search) return true;
    
    const searchLower = filters.search.toLowerCase();
    return (
      audit.Brand?.name?.toLowerCase().includes(searchLower) ||
      audit.prompt?.toLowerCase().includes(searchLower) ||
      audit.account_id?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>Audit</Title>
        </Col>
        <Col>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => loadAudits()}
            loading={loading}
          >
            Yenile
          </Button>
        </Col>
      </Row>

      {/* Action Buttons - Single brand audit only */}
      <Row justify="center" gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={16} md={12} lg={8} xl={6}>
          <Card 
            title="✨ Yeni Audit Başlat" 
            size="small" 
            style={{ height: '100%', border: '2px solid #1890ff' }}
          >
            <Select
              placeholder="Marka Seçip Audit Yap"
              style={{ width: '100%', marginBottom: 8 }}
              showSearch
              optionFilterProp="children"
              loading={auditLoading}
              onSelect={(brandId) => {
                const brand = brands.find(b => b.id === brandId);
                if (brand) handleNewAudit(brand);
              }}
            >
              {brands.map(brand => (
                <Option key={brand.id} value={brand.id}>
                  {brand.name}
                </Option>
              ))}
            </Select>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <PlusOutlined style={{ marginRight: 4 }} />
              Seçili marka için yeni audit başlat
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col span={5}>
            <Select
              placeholder="Marka Seçin"
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="children"
              value={filters.brandId}
              onChange={(value) => handleFilterChange('brandId', value)}
            >
              {brands.map(brand => (
                <Option key={brand.id} value={brand.id}>
                  {brand.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={3}>
            <Select
              placeholder="Audit Türü"
              style={{ width: '100%' }}
              allowClear
              value={filters.auditType}
              onChange={(value) => handleFilterChange('auditType', value)}
            >
              <Option value="google_ads">Google Ads</Option>
              <Option value="facebook">Facebook Ads</Option>
              <Option value="google_analytics">Google Analytics</Option>
              <Option value="custom_data">ReportsUp</Option>
            </Select>
          </Col>
          <Col span={3}>
            <Select
              placeholder="Durum"
              style={{ width: '100%' }}
              allowClear
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
            >
              <Option value="success">Başarılı</Option>
              <Option value="error">Hatalı</Option>
              <Option value="pending">Beklemede</Option>
            </Select>
          </Col>
          <Col span={5}>
            <RangePicker
              style={{ width: '100%' }}
              value={filters.dateRange}
              onChange={(dates) => handleFilterChange('dateRange', dates)}
              placeholder={['Başlangıç', 'Bitiş']}
            />
          </Col>
          <Col span={4}>
            <Input
              placeholder="Ara..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              onPressEnter={() => handleSearch(filters.search)}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Space>
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={() => loadAudits()}
                loading={loading}
              >
                Filtrele
              </Button>
              <Button onClick={clearFilters}>
                Temizle
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Audit Cards */}
      <Spin spinning={loading}>
        {filteredAudits.length === 0 ? (
          <Empty
            description="Henüz audit kaydı bulunamadı"
            style={{ margin: '40px 0' }}
          />
        ) : (
          <>
            <Row gutter={[16, 16]}>
              {filteredAudits.map(audit => (
                <Col span={24} key={audit.id}>
                  <AuditCard
                    audit={audit}
                    onViewDetails={handleViewDetails}
                    getStatusColor={getStatusColor}
                    getStatusText={getStatusText}
                    onCancelAudit={handleCancelAudit}
                    onDeleteAudit={handleDeleteAudit}
                    onToggleVisibility={handleToggleVisibility}
                  />
                </Col>
              ))}
            </Row>

            {/* Pagination */}
            <Row justify="center" style={{ marginTop: 24 }}>
              <Pagination
                current={pagination.current}
                total={pagination.total}
                pageSize={pagination.pageSize}
                showSizeChanger
                showQuickJumper
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} / ${total} audit`
                }
                onChange={handlePaginationChange}
                pageSizeOptions={['5', '10', '20', '50']}
              />
            </Row>
          </>
        )}
      </Spin>

      {/* Detail Modal */}
      <AuditModal
        visible={modalVisible}
        audit={selectedAudit}
        onClose={() => setModalVisible(false)}
        getStatusColor={getStatusColor}
        getStatusText={getStatusText}
      />

      {/* New Audit Modal */}
      <BrandAuditModal
        visible={newAuditModalVisible}
        brand={selectedBrandForAudit}
        capabilities={brandCapabilities}
        onCancel={() => {
          setNewAuditModalVisible(false);
          setSelectedBrandForAudit(null);
          setBrandCapabilities(null);
        }}
        onSuccess={handleAuditSuccess}
        onError={handleAuditError}
        setLoading={setAuditLoading}
      />
    </div>
  );
};

export default Audits;